/**
 * Change a tenant user's password on the standard slide panel.
 *
 * Ported from `change_password.tsx`: `Panel` + Kendo Form became
 * `FormSlidePanel`, `.then/.catch` became a `useMutation`, and the floating
 * error snackbar became the panel's sticky error banner — the same
 * presentation every other RHF form uses.
 */
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { AxiosError } from "axios";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { TlxHttpResponse, UserControllerService } from "@/api";
import { FormPassword } from "@/components/Forms";
import PasswordStrength from "@/components/PasswordStrength";
import { FormSlidePanel } from "@/components/slide-panel";
import { useAuthority } from "@/context/UserContext";
import useMutation from "@/hooks/useMutation";
import { validatePassword } from "@/libs/utils";

import "./style.scss";

export interface ChangePasswordFormValues {
  currentPassword?: string;
  newPassword?: string;
  newPasswordMatch?: string;
  [key: string]: unknown;
}

export interface ChangePasswordPanelProps {
  open: boolean;
  onClose: () => void;
  /** Login id of the account whose password is changed. */
  loginId?: string;
  onSaved: () => void;
}

const ChangePasswordPanel: React.FC<ChangePasswordPanelProps> = ({
  open,
  onClose,
  loginId,
  onSaved,
}) => {
  const { t } = useTranslation();
  const { hasAuthority } = useAuthority();
  const canEditPassword =
    hasAuthority("USER_PASSWORD:WRITE") || hasAuthority("USER_PASSWORD:ALL");
  const [errorMessage, setErrorMessage] =
    useState<AxiosError<TlxHttpResponse>>();

  const changePassword = useMutation({
    method: UserControllerService.changePasswordUsingPut,
    options: {
      onSuccess: () => onSaved(),
      onError: (error: AxiosError<TlxHttpResponse>) => setErrorMessage(error),
    },
  });

  const validate = (values: ChangePasswordFormValues) => {
    const thisFieldIsRequired = t("validation.required");
    const errors: Record<string, string | undefined> = {
      // On the field it describes, so a weak password both shows a message and
      // takes focus — a key with no rendered field blocks Save silently.
      newPassword: !values.newPassword
        ? thisFieldIsRequired
        : validatePassword(values.newPassword).isValid
          ? undefined
          : t("validation.passwordRequirements"),
      newPasswordMatch: values.newPasswordMatch
        ? values.newPasswordMatch === values.newPassword
          ? undefined
          : t("validation.confirmPassword")
        : thisFieldIsRequired,
    };
    // Without password-write authority the user is changing their own
    // password, so the current one is required. Length and strength rules
    // belong to the new password (above).
    if (!canEditPassword) {
      errors.currentPassword = values.currentPassword
        ? undefined
        : thisFieldIsRequired;
    }
    return errors;
  };

  const onSave = (values: ChangePasswordFormValues) => {
    setErrorMessage(undefined);
    // `currentPassword` is sent whether or not the field rendered — an admin
    // path posts `undefined`, which drops out during serialization. Same body
    // the legacy form produced.
    changePassword.mutate({
      changeUserPassword: {
        currentPassword: values.currentPassword,
        loginId,
        newPassword: values.newPassword as string,
      },
    });
  };

  return (
    <FormSlidePanel<ChangePasswordFormValues>
      open={open}
      onClose={onClose}
      size="sm"
      testId="user-change-password-panel"
      className="change-password-form"
      title={t("changePassword.title")}
      subtitle={loginId}
      saveText={t("changePassword.title")}
      isSubmitting={changePassword.isLoading}
      initialValues={{}}
      validate={validate}
      errorMessage={errorMessage}
      onSave={onSave}
    >
      {formApi => (
        <GridLayout
          cols={[{ width: "1fr" }, { width: "1fr" }]}
          gap={{ cols: 15, rows: 15 }}
        >
          <GridLayoutItem>
            {!canEditPassword && (
              <FormPassword
                name="currentPassword"
                required
                label={t("changePassword.currentPassword")}
                floatLabel={t("changePassword.currentPassword")}
                floatingLabel
              />
            )}
            <FormPassword
              name="newPassword"
              required
              label={t("changePassword.newPassword")}
              floatingLabel
            />
            <FormPassword
              name="newPasswordMatch"
              required
              label={t("changePassword.confirmNewPassword")}
              className="conditions-input"
              floatingLabel
            />
          </GridLayoutItem>
          <GridLayoutItem className="password-strength-container">
            <PasswordStrength
              password={formApi.valueGetter<string>("newPassword") ?? ""}
              isTouched={Object.keys(formApi.touched).length > 0}
            />
          </GridLayoutItem>
        </GridLayout>
      )}
    </FormSlidePanel>
  );
};

export default ChangePasswordPanel;
